import type { IBeneficiaryDetail, IBeneficiaryEntry } from "@/modules/beneficiary/common/types";
import { useGetBeneficiaryDetailsQuery } from "@/modules/beneficiary/services";
import { INITIAL_PAGE, INITIAL_PAGE_SIZE } from "@/shared/common/constants";
import { BeneficiaryType, CloudflareAsset } from "@/shared/common/enums";
import type { IBeneficiaryGroups, IBeneficiaryListOption, IError } from "@/shared/common/types";
import { CloudFlareImage, EmptyOrErrorContent } from "@/shared/components";
import { BeneficiaryGroup } from "@/shared/components/TransferBeneficiaries/BeneficiaryGroup";
import { formatCardNumber, getErrorMessage, isValidCard } from "@/shared/helpers";
import { useFormatedInputCursor } from "@/shared/hooks";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Spinner } from "@ab.uitools/ui-library/components/Spinner";
import { DROPDOWN_AND_INPUT_GAP } from "@ab.uitools/ui-library/consts";
import {
  useGetElemPositions,
  useGetElemSizes,
  useGetHasBottomSpace,
  useHideOnResize,
  useHideOnScroll,
  useOnOutsideClick,
} from "@ab.uitools/ui-library/hooks";
import {
  useChangePositionsOnScroll
} from "@ab.uitools/ui-library/hooks/useChangePositionsOnScroll";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";

interface Props {
  entryType: BeneficiaryType;
  name: string;
  className?: string;
  label?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  setBeneficiaryName?: Dispatch<SetStateAction<string>>;
}

export const TransferBeneficiaries = ({
  name,
  entryType,
  className,
  label,
  onChange,
  placeholder,
  setBeneficiaryName,
}: Props) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormProps();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [number, setNumber] = useState<string>("");
  const [dropdownRef, setDropdownRef] = useState<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const clickedNameRef = useRef<string>("");
  const { formatedInputOnChange } = useFormatedInputCursor(
    inputRef,
    (val) => isBeneficiaryCards ? formatCardNumber({ card: val }) : val,
  );
  const scrollContainer = document.getElementById("form-container");

  const {
    data: beneficiaryDetails,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetBeneficiaryDetailsQuery({
    entryType,
    pageIndex: INITIAL_PAGE,
    pageSize: INITIAL_PAGE_SIZE,
  }, { refetchOnMountOrArgChange: true });

  const numberValue = watch?.()[name];

  const isBeneficiaryCards = entryType === BeneficiaryType.Card;
  const closeDropdown = () => setIsOpen(false);

  const handleChange = (value: string) => {
    setBeneficiaryName?.("");
    const val = isBeneficiaryCards ? formatCardNumber({ card: value }) : value;
    setValue(name, val);
    setNumber(val);
    onChange?.(val);
  };

  const onItemClick = (value: string, name: string) => {
    clickedNameRef.current = name;
    handleChange(value);
    setBeneficiaryName?.(name);
    closeDropdown();
  };

  const beneficiaryGroups = useMemo((): IBeneficiaryGroups => {
    const owned: IBeneficiaryListOption[] = [];
    const shared: IBeneficiaryListOption[] = [];

    beneficiaryDetails?.forEach((item: IBeneficiaryDetail) => {
      const target = item.isCreatedByRequester ? owned : shared;
      item.entries?.forEach((entry: IBeneficiaryEntry) => {
        target.push({ name: item.name, number: entry.number, note: entry.note });
      });
    });

    return { owned, shared };
  }, [beneficiaryDetails]);

  const filteredGroups = useMemo((): IBeneficiaryGroups => {
    if (!searchValue.trim()) return beneficiaryGroups;
    const query = searchValue.toLowerCase();
    const filter = (list: IBeneficiaryListOption[]) =>
      list.filter(
        (b) =>
          b.name?.toLowerCase().includes(query) ||
          b.number?.toLowerCase().includes(query) ||
          b.note?.toLowerCase().includes(query)
      );
    return { owned: filter(beneficiaryGroups.owned), shared: filter(beneficiaryGroups.shared) };
  }, [beneficiaryGroups, searchValue]);

  const hasAnyBeneficiaries = !!beneficiaryGroups.owned.length || !!beneficiaryGroups.shared.length;
  const hasAnyResults = !!filteredGroups.owned.length || !!filteredGroups.shared.length;

  const { bottom, left, top } = useGetElemPositions(inputRef.current);
  const { width } = useGetElemSizes(containerRef.current);

  useOnOutsideClick([inputRef.current, dropdownRef], closeDropdown, isOpen, useId());

  const { hasBottomSpace } = useGetHasBottomSpace({
    element: dropdownRef,
    input: inputRef.current,
  });

  useChangePositionsOnScroll({
    parentElement: inputRef?.current,
    childElement: dropdownRef,
    hasBottomSpace,
  });
  useHideOnResize(closeDropdown);
  useHideOnScroll(closeDropdown, scrollContainer);

  const getMatchedName = (number: string) => {
    const allBeneficiaries = [...beneficiaryGroups.owned, ...beneficiaryGroups.shared];
    const matched = allBeneficiaries.find((b) => b.number === number);
    return matched?.name || "";
  };

  useEffect(() => {
    if (!setBeneficiaryName) return;
    if (clickedNameRef.current) {
      setBeneficiaryName(clickedNameRef.current);
    } else {
      const matchedName = getMatchedName(number);
      setBeneficiaryName(matchedName);
    }
  }, [number]);

  useEffect(() => {
    if (!setBeneficiaryName || !numberValue) return;
    if (clickedNameRef.current) {
      clickedNameRef.current = "";
      return;
    }
    const matchedName = getMatchedName(numberValue);
    if (matchedName) setBeneficiaryName(matchedName);
  }, [numberValue, beneficiaryGroups]);

  return (
    <div ref={containerRef} className="transfer-beneficiary">
      <FormField
        name={name}
        className={className}
        errorMessageIcon="infoFilled"
        As={(props) => {
          const { onChange, ...rest } = props;
          return (
            <Input
              {...rest}
              required
              isTrimValues
              label={label}
              ref={inputRef}
              handleChange={(_, value) => {
                clickedNameRef.current = "";
                handleChange(value);
              }}
              onFocus={closeDropdown}
              placeholder={placeholder}
              onChange={formatedInputOnChange(onChange)}
              isAllowed={({ formattedValue }) => isBeneficiaryCards ? isValidCard(formattedValue) : true}
              rightIconProps={{
                Component: (props) => (
                  <CloudFlareImage
                    {...props}
                    className={`${props.className} cursor-pointer`}
                    onClick={() => setIsOpen(true)}
                    path={CloudflareAsset.ProfileGroupGreen}
                  />
                ),
              }}
            />
          );
        }}
      />
      {isOpen && (
        <div
          className={`transfer-beneficiary__container transfer-beneficiary__container--${hasBottomSpace ? "bottom" : "top"}`}
          style={{
            left,
            width,
            ...(hasBottomSpace
              ? {
                top: bottom,
                maxHeight: Math.min(440, window.innerHeight - bottom - DROPDOWN_AND_INPUT_GAP)
              }
              : {
                bottom: window.innerHeight - top + DROPDOWN_AND_INPUT_GAP,
                maxHeight: Math.min(440, top - DROPDOWN_AND_INPUT_GAP),
              }),
          }}
          ref={setDropdownRef}
        >
          {isFetching ? (
            <div className="spinner-wrapper">
              <Spinner isFullScreen={false} size="small"/>
            </div>
          ) : !isFetching && (!hasAnyBeneficiaries || isError) ? (
            <div className="full-width full-height flexbox align-items--center p-16">
              <EmptyOrErrorContent
                isError={isError}
                title={isError ? t(getErrorMessage(error as IError)) : t("beneficiary.emptyTitle")}
                subtitle={!isError ? t("beneficiary.emptySubtitle") : ""}
                isShowButton={isError}
                action={refetch}
              />
            </div>
          ) : (
            <>
              <div className="px-16 py-16">
                <Input
                  placeholder={t("general.search")}
                  currentValue={searchValue}
                  leftIconProps={{
                    Component: (props) => (
                      <CloudFlareImage {...props} path={CloudflareAsset.Search}/>
                    ),
                  }}
                  handleChange={(_, v) => setSearchValue(v)}
                />
              </div>
              <div className="scrollbar scrollbar--vertical transfer-beneficiary__list pb-16">
                {!hasAnyResults ? (
                  <div className="full-width full-height flexbox align-items--center">
                    <EmptyOrErrorContent title={t("beneficiary.emptyTitle")} isShowButton={false}/>
                  </div>
                ) : (
                  <>
                    <BeneficiaryGroup
                      list={filteredGroups.owned}
                      label={t("beneficiary.owned")}
                      isBeneficiaryCards={isBeneficiaryCards}
                      onItemClick={onItemClick}
                    />
                    {filteredGroups.owned.length > 0 && filteredGroups.shared.length > 0 && (
                      <div className="px-16 py-8">
                        <Divider color="light" type="primary" isHorizontal/>
                      </div>
                    )}
                    <BeneficiaryGroup
                      list={filteredGroups.shared}
                      label={t("beneficiary.shared")}
                      isBeneficiaryCards={isBeneficiaryCards}
                      onItemClick={onItemClick}
                    />
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
